import React, { memo } from 'react';
import { format } from 'date-fns';
import { DayColumnProps } from './types';

// Add type declaration for the window object to include our custom property
declare global {
  interface Window {
    eventBeingEdited?: boolean;
  }
}

const DayColumn = memo<DayColumnProps>(({ date, events, onAddEvent, onDeleteEvent }) => {
  const formattedDate = format(date, 'EEE, MMM d');
  const dayEvents = events.filter(event => {
    const eventDate = new Date(event.start);
    // Ensure we compare only date parts (not time)
    const sameDay = (
      date.getFullYear() === eventDate.getFullYear() &&
      date.getMonth() === eventDate.getMonth() &&
      date.getDate() === eventDate.getDate()
    );
    
    // console.log(`Comparing dates for ${event.title}:`, {
    //   columnDate: format(date, 'yyyy-MM-dd'),
    //   eventDate: format(eventDate, 'yyyy-MM-dd'),
    //   isSameDay: sameDay
    // });
    
    return sameDay;
  });
  
  return (
    <div className="flex-1 border-l border-gray-200 min-h-[600px] relative">
      <div className="h-12 flex items-center justify-center border-b border-gray-200 bg-gray-50">
        {formattedDate}
      </div>
      <div className="h-[600px] p-1 relative">
        {/* Hour grid lines */}
        {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
          <div 
            key={hour} 
            className="absolute w-full border-t border-gray-100" 
            style={{ top: `${(hour - 8) * 50}px`, height: '50px', left: 0 }}
          ></div>
        ))}
        {dayEvents.map(event => {
          // Calculate position and height based on event time
          const startTime = new Date(event.start);
          const endTime = new Date(event.end);
          const startHour = startTime.getHours() + startTime.getMinutes() / 60;
          const endHour = endTime.getHours() + endTime.getMinutes() / 60;
          
          // Position events from 8AM (0px) to 8PM (600px)
          const top = Math.max(0, (startHour - 8) * 50); // 50px per hour
          const height = Math.max(25, (endHour - startHour) * 50); // at least 25px height
          
          return (
          <div 
            key={event.id}
            style={{
              position: 'absolute',
              top: `${top}px`,
              height: `${height}px`,
              left: '8px',
              right: '8px',
              zIndex: 10
            }}
            className={`p-2 text-xs rounded ${event.isBlockedTime ? 'bg-gray-200 border-2 border-dashed border-red-400 text-gray-700' : 'text-white'} ${
              // Different styling for blocked time vs regular appointments
              !event.isBlockedTime ? (
                event.status === 'scheduled' ? 'bg-green-600' :
                event.status === 'pending_payment' ? 'bg-yellow-500' :
                event.status === 'completed' ? 'bg-blue-500' :
                event.status === 'cancelled' ? 'bg-red-500' :
                event.status === 'no_show' ? 'bg-gray-500' : 'bg-blue-500'
              ) : ''
            } relative group overflow-y-auto`}
          >
            {/* Delete button - shown on hover */}
            {onDeleteEvent && (
              <button 
                className="absolute top-1 right-1 text-white bg-red-500 rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the edit event
                  if (window.confirm('Are you sure you want to delete this event?')) {
                    onDeleteEvent(event.id);
                  }
                }}
                title="Delete event"
              >
                ×
              </button>
            )}
            
            <div
              className="cursor-pointer"
              onClick={() => {
                if (event.isBlockedTime) {
                  // For blocked times, open edit modal
                  window.eventBeingEdited = true;
                  window.dispatchEvent(new CustomEvent('edit-event', { detail: event }));
                } else {
                  // For appointments, open details modal
                  window.dispatchEvent(new CustomEvent('view-appointment-details', { detail: event }));
                }
              }}
            >
              <div className="font-medium text-xs truncate">{event.title}</div>
              <div className="text-xs">
                {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
              </div>
              {event.clientPhone && <div className="text-xs truncate">{event.clientPhone}</div>}
              {event.isBlockedTime && <div className="text-red-500 font-medium text-xs mt-1">BLOCKED TIME</div>}
              {!event.isBlockedTime && event.paymentStatus && (
                <div className="mt-1">
                  {event.paymentStatus === 'paid' && (
                    <span className="inline-block px-1.5 py-0.5 bg-green-200 text-green-900 text-xs font-semibold rounded">Paid</span>
                  )}
                  {event.paymentStatus === 'pending_payment' && (
                    <span className="inline-block px-1.5 py-0.5 bg-yellow-200 text-yellow-900 text-xs font-semibold rounded">Pending</span>
                  )}
                  {event.paymentStatus === 'failed' && (
                    <span className="inline-block px-1.5 py-0.5 bg-red-200 text-red-900 text-xs font-semibold rounded">Failed</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )})}
        
        <button 
          className="absolute bottom-4 left-0 right-0 mx-2 p-1 text-xs border border-dashed border-gray-300 rounded hover:border-gray-400 text-gray-500"
          onClick={() => onAddEvent(date)}
        >
          + Add
        </button>
      </div>
    </div>
  );
});

export default DayColumn;